import { useTranslation } from '../../../i18n';
import { formatMoney, PLATFORM_CURRENCY } from '../../../utils/money';
import { localName } from '../../../utils/format';
import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Progress, Row, Col, Space, Tag, Tooltip, Typography, Statistic, Button, Modal, InputNumber, theme } from 'antd';
import ReactECharts from 'echarts-for-react';
import { formatDateTime } from '../../../utils/date';
import { formatMemory, formatMB, formatBps, ok, runAction } from './utils';
import { VmRenewInline, daysLeft } from '../RenewControls';

const { Text } = Typography;

/** Tiny trend line for the overview stat cards. */
const Spark = ({ values, color }) => (
    <ReactECharts
        style={{ height: 36 }}
        opts={{ renderer: 'svg' }}
        option={{
            grid: { left: 0, right: 0, top: 4, bottom: 2 },
            xAxis: { type: 'category', show: false, boundaryGap: false },
            yAxis: { type: 'value', show: false, min: 0 },
            series: [{
                type: 'line', data: values, showSymbol: false, smooth: true,
                lineStyle: { width: 1.5, color }, areaStyle: { color, opacity: 0.12 },
            }],
            animation: false,
        }}
    />
);

const V6_REASON = { plan: 'Plan has no IPv6', region: 'IPv6 not yet available in this region', capacity: 'Addresses temporarily exhausted' };

/** Last-hour snapshot: current value + trend for the key metrics. */
const LiveStats = ({ id, api, active, isMobile, onMore }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!active || !api?.getVmMetrics) return undefined;
        let alive = true;
        const load = async () => {
            const res = await api.getVmMetrics(id, '1h');
            if (alive) setData(ok(res) ? res.data : { available: false });
        };
        load();
        const t = setInterval(load, 60000);
        return () => { alive = false; clearInterval(t); };
    }, [id, api, active]);

    // No data yet: same cards with "-" and flat charts, no explanation text.
    const points = (data?.available && data.points) || [];
    const last = points[points.length - 1] || {};
    const memPct = last.mem_total_mb ? (100 * last.mem_used_mb) / last.mem_total_mb : null;
    const items = [
        { title: 'CPU', value: last.cpu != null ? `${Number(last.cpu).toFixed(1)}%` : '-', series: points.map((p) => p.cpu), color: token.colorPrimary },
        {
            title: t('Memory'),
            value: memPct != null ? `${memPct.toFixed(0)}%` : formatMemory(last.mem_used_mb),
            sub: last.mem_total_mb ? `${formatMemory(last.mem_used_mb)} / ${formatMemory(last.mem_total_mb)}` : null,
            series: points.map((p) => (p.mem_total_mb ? (100 * p.mem_used_mb) / p.mem_total_mb : p.mem_used_mb)),
            color: token.colorSuccess,
        },
        { title: t('Public network in/out'), value: `${formatBps(last.net_in_bps)} / ${formatBps(last.net_out_bps)}`, series: points.map((p) => (p.net_in_bps || 0) + (p.net_out_bps || 0)), color: token.colorWarning },
        { title: t('Disk read/write'), value: `${formatBps(last.disk_read_bps)} / ${formatBps(last.disk_write_bps)}`, series: points.map((p) => (p.disk_read_bps || 0) + (p.disk_write_bps || 0)), color: token.colorInfo },
    ];
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong>{t('Real-time')}</Text>
                <Button type="link" size="small" onClick={onMore} style={{ paddingRight: 0 }}>{t('More monitoring')}</Button>
            </div>
            <Row gutter={[12, 12]}>
                {items.map((it) => (
                    <Col xs={12} md={6} key={it.title}>
                        <Card size="small" styles={{ body: { paddingBottom: 6 } }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>{it.title}</Text>
                            <div style={{ fontSize: isMobile ? 15 : 18, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.value}</div>
                            {it.sub && <Text type="secondary" style={{ fontSize: 11 }}>{it.sub}</Text>}
                            <Spark values={it.series} color={it.color} />
                        </Card>
                    </Col>
                ))}
            </Row>
        </div>
    );
};

const TRAFFIC_OVER = { throttle: 'Throttled until reset', cut_public: 'Public network cut off', shutdown: 'Powered off' };

/** Traffic pack purchase (valid until the next reset, applied immediately). */
const TrafficPackButton = ({ id, api, onDone }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [qty, setQty] = useState(1);
    const [quote, setQuote] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return undefined;
        let alive = true;
        api.getTrafficPack(id, qty).then((r) => { if (alive) setQuote(ok(r) ? r.data : null); });
        return () => { alive = false; };
    }, [open, qty, id, api]);

    if (!api?.getTrafficPack) return null;
    const buy = async () => {
        setBusy(true);
        const res = await runAction(() => api.buyTrafficPack(id, qty), t('Active'), t('Purchase failed'));
        setBusy(false);
        if (res) {
            setOpen(false);
            onDone?.(res.data);
        }
    };
    return (
        <>
            <Button size="small" onClick={() => setOpen(true)}>{t('Buy traffic pack')}</Button>
            <Modal
                title={t('Buy traffic pack')}
                open={open}
                onCancel={() => setOpen(false)}
                onOk={buy}
                okText={t('Buy')}
                confirmLoading={busy}
                okButtonProps={{ disabled: !quote?.enabled || !quote?.available }}
                width={380}
            >
                {quote && !(quote.enabled && quote.available) ? (
                    <Text type="secondary">{t('Not yet available')}</Text>
                ) : (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Space>
                            <InputNumber min={1} max={quote?.max_qty || 10} value={qty} onChange={(v) => setQty(v || 1)} />
                            <Text>× {quote?.unit_gb ?? '-'} GB</Text>
                        </Space>
                        <Text strong>{quote ? formatMoney(quote.amount, quote.currency || PLATFORM_CURRENCY) : '-'}</Text>
                        {quote?.valid_until && <Text type="secondary" style={{ fontSize: 12 }}>{t('Valid until {{time}}', { time: formatDateTime(quote.valid_until) })}</Text>}
                    </Space>
                )}
            </Modal>
        </>
    );
};

const OverviewTab = ({ vm, row, mode, isMobile, id, api, active = true, onMore, onChanged }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const disks = vm.disks || [];
    const diskGb = row?.disk_gb || disks.reduce((s, d) => s + (Number(d.size) || 0), 0);
    const traffic = row?.traffic || {
        used_mb: (Number(vm.flow_used) || 0) * 1024,
        limit_mb: (Number(vm.flow_limit) || 0) * 1024,
        reset_at: vm.flow_reset_at,
    };
    const limit = Number(traffic.limit_mb) || 0;
    const used = Number(traffic.used_mb) || 0;
    const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
    const expiresAt = row?.expires_at ?? vm.expires_at;
    const owner = row?.owner || vm.owner;
    // VM's own IPs plus EIPs (row.ips includes EIP addresses).
    const ips = [...(vm.ips || [])];
    // No IPv6 on the VM: say why (plan | region | capacity); admin can 补发 the plan's block.
    const v6None = !!vm.ipv6?.reason && !ips.some((ip) => ip.type === 'v6');
    const [backfilling, setBackfilling] = useState(false);
    const backfill = async () => {
        setBackfilling(true);
        const res = await runAction(() => api.backfillIpv6(id ?? vm.id), t('Assigned; takes effect after the instance restarts'), t('Assignment failed'));
        setBackfilling(false);
        if (res) onChanged?.();
    };
    (row?.ips || []).forEach((address) => {
        if (!ips.some((i) => i.address === address)) ips.push({ address, type: 'eip' });
    });

    const specs = [
        { title: 'CPU', value: row?.vds ? t('{{n}} dedicated cores', { n: row.vds.vcpus || vm.core }) : t('{{value}} cores', { value: vm.core || '-' }) },
        { title: t('Memory'), value: formatMemory(vm.memory) },
        { title: t('Disk'), value: diskGb ? `${diskGb} GB` : '-' },
        { title: t('Bandwidth'), value: Number(vm.net_out) > 0 ? `${vm.net_out} Mbps` : t('Unlimited') },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row gutter={[12, 12]}>
                {specs.map((s) => (
                    <Col xs={12} md={6} key={s.title}>
                        <Card size="small">
                            <Statistic title={s.title} value={s.value} valueStyle={{ fontSize: isMobile ? 18 : 22 }} />
                        </Card>
                    </Col>
                ))}
            </Row>

            <LiveStats id={id ?? vm.id} api={api} active={active} isMobile={isMobile} onMore={onMore} />

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={14}>
                    <Card size="small" title={t('Basic information')}>
                        <Descriptions column={isMobile ? 1 : 2} size="small" labelStyle={{ color: token.colorTextSecondary }}>
                            <Descriptions.Item label={t('System')}>{vm.image?.name || '-'}{vm.image?.license_notice && <div><Text type="warning" style={{ fontSize: 12 }}>{t('Windows requires your own license activation (evaluation: 180 days)')}</Text></div>}</Descriptions.Item>
                            <Descriptions.Item label={t('Username')}><Text copyable>{vm.login_user || 'root'}</Text></Descriptions.Item>
                            <Descriptions.Item label={t('Region')}>{row?.region || '-'}</Descriptions.Item>
                            {(row?.plan || vm.plan) && (
                                <Descriptions.Item label={t('Plan')}>{localName(row?.plan?.name || row?.plan || vm.plan)}</Descriptions.Item>
                            )}
                            {row?.vds && (
                                <Descriptions.Item label="VDS">
                                    <Space size={4} wrap>
                                        <Tag color="purple" style={{ marginInlineEnd: 0 }}>{t('{{n}} dedicated cores', { n: row.vds.vcpus || vm.core })}</Tag>
                                        {row.vds.model && <Text>{row.vds.model}</Text>}
                                        <Text type="secondary">{t('Nested virtualization')} {row.vds.nested ? t('On') : t('Off')}</Text>
                                    </Space>
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label={t('Created at')}>{formatDateTime(vm.created_at)}</Descriptions.Item>
                            {expiresAt && (
                                <Descriptions.Item label={t('Expires at')} span={mode === 'user' && !isMobile ? 2 : 1}>
                                    <Space size={12} wrap>
                                        <Text type={daysLeft(expiresAt) <= 7 ? 'danger' : undefined}>{formatDateTime(expiresAt)}</Text>
                                        {mode === 'user' && api?.getVmBilling && <VmRenewInline vmId={id ?? vm.id} api={api} />}
                                    </Space>
                                </Descriptions.Item>
                            )}
                            {mode === 'admin' && (
                                <>
                                    <Descriptions.Item label={t('Owner')}>
                                        {owner ? (
                                            <span>{owner.username || `#${owner.id}`}{owner.email ? <Text type="secondary"> · {owner.email}</Text> : null}</span>
                                        ) : '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('Node')}>{vm.node?.name || row?.node?.name || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="UUID" span={isMobile ? 1 : 2}>
                                        <Text copyable style={{ fontSize: 12 }}>{vm.uuid || '-'}</Text>
                                    </Descriptions.Item>
                                </>
                            )}
                        </Descriptions>
                    </Card>
                </Col>
                <Col xs={24} lg={10}>
                    <Card
                        size="small"
                        title={t('Traffic this month')}
                        extra={mode === 'user' && limit > 0 ? <TrafficPackButton id={id ?? vm.id} api={api} onDone={onChanged} /> : null}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <Text strong>{formatMB(used)}</Text>
                            <Text type="secondary">{limit > 0 ? formatMB(limit) : t('Unlimited')}</Text>
                        </div>
                        <Progress
                            percent={limit > 0 ? +pct.toFixed(1) : 0}
                            showInfo={limit > 0}
                            status={pct >= 100 ? 'exception' : 'normal'}
                            strokeColor={pct >= 90 ? token.colorError : undefined}
                        />
                        {traffic.level === 'over' && traffic.action && (
                            <Tag color="warning" style={{ marginBottom: 4 }}>{t(TRAFFIC_OVER[traffic.action] || 'Over quota')}</Tag>
                        )}
                        {traffic.reset_at && (
                            <Text type="secondary" style={{ fontSize: 12 }}>{t('Resets at {{time}}', { time: formatDateTime(traffic.reset_at) })}</Text>
                        )}
                    </Card>
                </Col>
            </Row>

            <Card size="small" title={t('IP address')}>
                {ips.length === 0 ? (
                    <Text type="secondary">{t('None yet')}</Text>
                ) : (
                    <Space wrap size={[16, 8]}>
                        {ips.map((ip) => (
                            <Space key={ip.id || ip.address} size={4}>
                                <Text copyable={{ text: ip.address }} style={{ fontFamily: token.fontFamilyCode }}>{ip.address}</Text>
                                {ip.is_main && <Tag color="blue">{t('Primary')}</Tag>}
                                {ip.type && <Tag>{String(ip.type).toUpperCase()}</Tag>}
                                {ip.type === 'v6' && ip.prefix && <Text type="secondary" copyable={{ text: ip.prefix }} style={{ fontFamily: token.fontFamilyCode, fontSize: 12 }}>{ip.prefix}</Text>}
                            </Space>
                        ))}
                        {(vm.ipv6_blocks || []).map((b) => (
                            <Space key={b.prefix} size={4}>
                                <Text copyable={{ text: b.prefix }} style={{ fontFamily: token.fontFamilyCode }}>{b.prefix}</Text>
                                <Tag>IPv6</Tag>
                            </Space>
                        ))}
                    </Space>
                )}
                {v6None && (
                    <div style={{ marginTop: ips.length ? 8 : 0, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                        <Tag>IPv6</Tag>
                        <Tooltip title={vm.ipv6.missing > 0 ? t('Assigned free of charge once available') : null}>
                            <Text type="secondary">{t('Not assigned')} · {t(V6_REASON[vm.ipv6.reason] || 'Not assigned')}</Text>
                        </Tooltip>
                        {mode === 'admin' && vm.ipv6.missing > 0 && api?.backfillIpv6 && (
                            <Button size="small" type="link" loading={backfilling} onClick={backfill}>{t('Assign IPv6')}</Button>
                        )}
                    </div>
                )}
            </Card>
        </Space>
    );
};

export default OverviewTab;
